<template>
  <component-wrapper :class="['retailer-search-fi', theme ? `retailer-search-fi--${theme}` : null]">
    <template v-if="isMounted">
      <div
        v-if="userLocationError"
        class="retailer-search-fi__user-location-error"
        :class="[
          theme
            ? 'fi-col-span-full'
            : 'fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12',
        ]"
      >
        <block-notification-fi
          close-btn
          type="error"
          icon="alert-triangle"
          class="fi-mb-30"
        >
          {{ $dict.get('dealer-Locator.User-Location.Error') }}
        </block-notification-fi>
      </div>

      <div
        :class="{ 'retailer-search-fi__map': true, 'retailer-search-fi__map--app-view': isAppView }"
      >
        <loading-indicator-wrapper
          :loading="directions.loading"
          variant="spinnerLocal"
          spinoff="in-relative-centered"
        >
          <GMapMap
            ref="gmap"
            :center="{ lat: googleMaps.lat, lng: googleMaps.lng }"
            :zoom="googleMaps.zoom"
            :options="googleMaps.googleMapsOptions"
            @zoom_changed="changeZoom"
          >
            <GMapCluster
              ref="cluster"
              v-memo="[directions, displayedRetailers]"
              :class="{
                'retailer-search-fi__cluster': true,
                'retailer-search-fi__cluster--results':
                  retailersCount > 0 || directions.markers?.length > 0 ? 1 : 0,
              }"
              :zoom-on-click="true"
              :grid-size="120"
              :max-zoom="15"
              :minimum-cluster-size="2"
              :renderer="clusterRenderer"
            >
              <GMapAdvancedMarker
                v-for="(marker, index) in directions.markers"
                :key="index"
                :gmp-draggable="marker.draggable"
                :position="marker.position"
                :icon="getMarkerIconDirection(marker.icon)"
                :title="marker.name"
                @dragend="markerDragged"
              />
              <map-markers-fi
                v-if="displayedRetailers?.length > 0"
                :displayed-retailers
                @activate-marker="activateMarker"
                @redraw="redrawMarkers"
              />
            </GMapCluster>
            <google-map-fetcher-fi @map="google = $event" />
          </GMapMap>
        </loading-indicator-wrapper>

        <div
          :class="{
            'retailer-search-fi__actions': true,
            'retailer-search-fi__actions--navigation-active': directions.active,
          }"
        >
          <retailer-search-filter-fi
            class="retailer-search-fi__filters"
            :location="location"
            :location-error="locationError"
            :retailer-name="retailerName"
            :autocomplete-options="googleMaps.autoCompleteOptions"
            :ranges="retailerRanges"
            :default-range="retailerDefaultRange"
            :theme="theme"
            :retailer-search-page="retailerSearchPage"
            :retailer-types="retailerTypes"
            :retailers-count="retailersCount"
            :navigation-active="directions.active"
            @location-changed="locationChanged"
            @retailer-name-changed="retailerNameChanged"
            @range-changed="setRange"
            @user-location-error="userLocationErrorChanged"
            @type-changed="retailerTypeChanged"
          />

          <div class="retailer-search-fi__directions">
            <div class="retailer-search-fi__directions__wrapper">
              <div
                v-if="routeData"
                class="retailer-search-fi__directions__info"
              >
                <span
                  v-if="routeData.distance?.text"
                  class="retailer-search-fi__directions__info__distance"
                >
                  <icon-fi
                    class="retailer-search-fi__directions__icon"
                    icon="car"
                  />
                  {{ routeData.distance.text }}
                </span>
                <span
                  v-if="routeData.duration?.text"
                  class="retailer-search-fi__directions__info__duration"
                >
                  {{ routeData.duration.text }}
                </span>
              </div>

              <div
                class="retailer-search-fi__directions__close"
                @click="closeDirections"
              >
                <icon-fi icon="close" />
              </div>
            </div>

            <div
              class="retailer-search-fi__directions__maps"
              @click="openGoogleMaps"
            >
              <icon-fi
                icon="google-map-pin"
                class="retailer-search-fi__directions__icon"
              />
              {{ $dict.get('dealer-Locator.Open-In-Google-Maps') }}
            </div>
          </div>
        </div>

        <transition
          mode="in-out"
          appear
          :enter-active-class="`animated ${isSM ? 'fadeInFromRight' : 'slideInFromBottom'} faster`"
          :leave-active-class="`animated ${isSM ? 'fadeOutToRight' : 'slideOutToBottom'} faster`"
        >
          <div
            v-if="googleMaps.infoWindowOpened"
            :class="{
              'retailer-search-fi__map__info-window': true,
              'retailer-search-fi__map__info-window--partner':
                googleMaps.marker.Retailer.PartnerType,
            }"
          >
            <div class="retailer-search-fi__map__info-window__close">
              <icon-fi
                icon="close"
                @click="closeInfoWindow()"
              />
            </div>

            <div class="retailer-search-fi__map__info-window__inner">
              <retailer-detail-fi
                :retailer="googleMaps.marker.Retailer"
                :is-stocked="googleMaps.marker.IsStocked"
                :distance="googleMaps.marker.Distance"
                :unit="googleMaps.marker.Unit"
                :show-stock="haveArticles"
                @navigate-to="navigateTo"
              />

              <div
                v-if="googleMaps.marker.Retailer.PartnerType"
                class="retailer-search-fi__map__info-window__partner"
              >
                <cloud-image-fi
                  :field="fields.partnerTypeLogo"
                  class="retailer-search-fi__map__info-window__partner__img"
                />
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="isEditMode"
        class="retailer-search-fi__disclaimer"
        v-html="fields.disclaimer?.editable"
      />
      <div
        v-if="!isEditMode && fields.disclaimer?.value"
        class="retailer-search-fi__disclaimer"
        v-html="fields.disclaimer?.value"
      />
      <!-- eslint-enable vue/no-v-html -->

      <div
        v-if="loading"
        class="retailer-search-fi__retailers__loading"
      >
        <loading-indicator
          class="assisted-service-company-fi__c-spinner"
          variant="spinnerLocal"
        />
      </div>

      <div
        v-if="!noRetailersFound"
        class="retailer-search-fi__retailers"
      >
        <grid-columns-fi :columns="8">
          <retailer-search-result-fi
            v-if="retailers"
            class="fi-col-span-full"
            :retailers="retailers"
            :show-stock="haveArticles"
            :partner-type-logo="fields.partnerTypeLogo"
            :show-distance="haveLocation"
            :theme="theme"
            @clicked="activateResultItem"
          />
        </grid-columns-fi>
      </div>

      <div
        v-else
        class="fi-col-span-full"
      >
        <div class="retailer-search-fi__no-results">
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="isEditMode"
            class="retailer-search-fi__no-results__headline"
            v-html="fields.noResultsHeadline?.editable"
          />
          <div
            v-else
            class="retailer-search-fi__no-results__headline"
            v-html="fields.noResultsHeadline?.value"
          />
          <div
            v-if="isEditMode"
            class="retailer-search-fi__no-results__text"
            v-html="fields.noResultsText?.editable"
          />
          <div
            v-else
            class="retailer-search-fi__no-results__text"
            v-html="fields.noResultsText?.value"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
      </div>
    </template>
    <loading-indicator
      v-else
      variant="spinnerLocal"
    />
  </component-wrapper>
</template>

<script setup lang="ts">
import { useClientHandle } from '@urql/vue';
import { IconFi, LoadingIndicator, LoadingIndicatorWrapper } from 'atoms';
import { throttle } from 'throttle-debounce';
import { computed, onBeforeUnmount, onMounted, reactive, ref, useTemplateRef, watch } from 'vue';
import { GMapAdvancedMarker, GMapCluster } from 'vue-google-maps-community-fork';

import CloudImageFi from '@/components/atoms/cloud-image-fi/CloudImageFi.vue';
import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GMapMap from '@/components/google-maps/GMapMap';
import GoogleMapFetcherFi from '@/components/google-maps/GoogleMapFetcherFi.vue';
import GridColumnsFi from '@/components/grid-fi/GridColumnsFi.vue';
import BlockNotificationFi from '@/components/notifications-fi/block-notification-fi/BlockNotificationFi.vue';
import {
  getDirectionsMarkerIcon,
  getRetailerMarkerIcon,
} from '@/components/retailer-search-fi/GetMarkerIconFi';
import googleMapsOptions, {
  type GoogleMapsOptions,
} from '@/components/retailer-search-fi/GoogleMapsOptions';
import RetailerSearchFilterFi from '@/components/retailer-search-fi/retailer-search-filter-fi/RetailerSearchFilterFi.vue';
import RetailerDetailFi from '@/components/retailer-search-fi/retailer-search-result-fi/RetailerDetailFi.vue';
import RetailerSearchResultFi from '@/components/retailer-search-fi/retailer-search-result-fi/RetailerSearchResultFi.vue';
import { useAppEvent } from '@/composables/appEvent';
import { useAssetUrl } from '@/composables/assetUrl';
import { useBreakpoints } from '@/composables/breakpoint';
import { useDistance } from '@/composables/distance';
import { useEventHub } from '@/composables/eventHub';
import { useFields } from '@/composables/fields';
import { useDict, useFischer } from '@/composables/fischerPlugin';
import { useSmoothScroll } from '@/composables/smoothScroll';
import GETRETAILERSEARCH from '@/graphql/getRetailerSearch.gql';
import type {
  CheckboxField,
  ImageField,
  MultiLineTextField,
  SingleLineTextField,
} from '@/sitecoreFieldTypes';

import MapMarkersFi from './MapMarkersFi.vue';

defineOptions({ name: 'RetailerSearchFi' });

const props = withDefaults(
  defineProps<{
    productIds?: string | number | any[] | null;
    countryList?: any[];
    defaultCountry?: string | null;
    ranges?: any[] | null;
    defaultRange?: string;
    theme?: string | null;
    retailerSearchPage?: boolean;
  }>(),
  {
    productIds: null,
    countryList: () => [],
    defaultCountry: null,
    ranges: null,
    defaultRange: '10',
    theme: null,
    retailerSearchPage: false,
  },
);

// Rendered on the retailer search page, the product detail page and the product family page.
// Only the retailer search page defines all of these, so every field may be missing.
export interface RetailerSearchFields {
  campaignRetailers?: CheckboxField;
  disclaimer?: MultiLineTextField;
  hiddenRetailers?: CheckboxField;
  noResultsHeadline?: SingleLineTextField;
  noResultsText?: MultiLineTextField;
  partnerTypeLogo?: ImageField;
}

const fields = useFields<RetailerSearchFields>();
const fischer = useFischer();
const dict = useDict();
const { getAssetUrl } = useAssetUrl();
const { unit, lengthUnitDistance } = useDistance();
const { sendEvent } = useEventHub();
const { isSM } = useBreakpoints();
const handle = useClientHandle();
const { smoothScrollToElement } = useSmoothScroll();
const { sendAppEvent } = useAppEvent();

const logDebug = (msg: any) => {
  if (!IS_SSR && (window as any).debugMaps) {
    console.debug(msg);
  }
};

const svg = `<img src="${getAssetUrl('/img/google-maps/cluster.svg')}">`;
const clusterRenderer = {
  render: ({ count, position }: any) => {
    const clusterDiv = document.createElement('div');
    clusterDiv.className = 'retailer-search-fi__cluster-element';
    clusterDiv.innerHTML = svg;

    const label = document.createElement('div');
    label.className = 'retailer-search-fi__cluster-label';
    label.textContent = String(count);
    clusterDiv.appendChild(label);

    return new window.google.maps.marker.AdvancedMarkerElement({
      position,
      content: clusterDiv,
      zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
    });
  },
};

const displayedRetailers = ref<any>();

const gmap = useTemplateRef<any>('gmap');
const cluster = useTemplateRef<any>('cluster');

const retailerName = ref('');
const location = ref<any>(null);
const isSearching = ref(false);
const locationError = ref(false);
const userLocationError = ref(false);
const showroomFallbackRetailer = ref<any>(null);
const range = ref<string | null>(null);
const noRetailersFound = ref(false);
const country = ref<string | null>(null);
const countryLocations = ref<any>(null);
const currentLocation = ref<any>(null);
const directionsService = ref<any>(null);
const directionsDisplay = ref<any>(null);
const routeData = ref<any>(null);
const directions = reactive({
  loading: false,
  active: false,
  origin: null as any,
  destination: null as any,
  markers: [] as any[],
  travelMode: 'DRIVING',
});
const google = ref<any>(null);
const googleMaps = reactive({
  url: 'https://maps.googleapis.com/maps/api/geocode/json?latlng',
  lat: 0,
  lng: 0,
  newLat: 0,
  newLng: 0,
  defaultLat: 0,
  defaultLng: 0,
  defaultZoom: 6,
  zoom: 6,
  languageIsoCode: null as string | null,
  countries: null as any,
  autoCompleteOptions: {
    componentRestrictions: {
      country: null as any,
    },
  },
  googleMapsOptions: { ...googleMapsOptions } as GoogleMapsOptions,
  infoWindowOpened: false,
  markerRefArray: [] as any[],
  marker: {
    partnerType: false,
    Retailer: {} as any,
    Distance: null as any,
    Unit: null as any,
    IsStocked: false,
  },
  facets: {
    facetsRetailerTypes: null as any,
    retailerTypes: [] as any,
    availabilityStatus: '',
    partnerTypes: null as any,
  },
  clusterMarkerOptionStyles: [] as any[],
});
const retailers = ref<any>(null);
const retailersCount = ref<any>(null);
const throttledGetRetailersByLocation = ref<any>(null);
const throttledChangeZoom = ref<any>(null);
const loading = ref(false);
const activeRetailer = ref<any>(null);
const isMounted = ref(false);
const lastRetailersHash = ref<any>(null);

let _currentSearchBounds: any = null;
let _lastSearchLat: any = null;
let _lastSearchLng: any = null;

const retailerRanges = computed(() => {
  if (props.ranges) {
    return props.ranges;
  }
  return [
    { name: `10 ${unit.value}`, value: '10' },
    { name: `25 ${unit.value}`, value: '25' },
    { name: `50 ${unit.value}`, value: '50' },
    { name: `75 ${unit.value}`, value: '75' },
    { name: `100 ${unit.value}`, value: '100' },
  ];
});

const retailerDefaultRange = computed(() => {
  if (retailerRanges.value && !retailerRanges.value.some((el) => el.value === props.defaultRange)) {
    return retailerRanges.value[0].value;
  }
  return props.defaultRange;
});

const haveLocation = computed(
  () =>
    !(
      (googleMaps.lat === 0 && googleMaps.lng === 0) ||
      (googleMaps.lat === googleMaps.defaultLat && googleMaps.lng === googleMaps.defaultLng)
    ),
);

const haveArticles = computed(() => {
  if (props.productIds) {
    if (typeof props.productIds === 'number' || (props.productIds as any).length) {
      return true;
    }
  }
  return false;
});

const countries = computed(() => {
  if (props.countryList?.length) {
    return props.countryList.map((country) => country.value);
  }
  return [];
});

const retailerTypes = computed(() => {
  if (googleMaps.facets.facetsRetailerTypes?.length) {
    const facets = [
      {
        name: dict.get('dealer-Locator.Facets.Labels.Retailer-Types') || '',
        elements: [] as any[],
        multiselect: true,
      },
    ];

    googleMaps.facets.facetsRetailerTypes.forEach((facet: any) => {
      const subElements: any[] = [];
      if (facet.Facets?.length) {
        facet.Facets.forEach((subFacet: any) => {
          const isSelected = subFacet.Selected.toString() === 'true';
          subElements.push({
            name: subFacet.Name,
            type: 'partner',
            selected: isSelected,
            count: subFacet.RecordCount,
          });
        });
      }

      facets[0].elements.push({
        name: facet.Name,
        type: 'retailer',
        selected: facet.Selected,
        count: facet.RecordCount,
        subElements,
      });
    });

    return facets;
  }
  return [];
});

watch(activeRetailer, async (newRetailer, oldRetailer) => {
  [oldRetailer, newRetailer].forEach((updatedRetailer) => {
    if (!updatedRetailer) return;
    const displayedRetailer = displayedRetailers.value?.find(
      (item: any) => item.Retailer.RetailerId === updatedRetailer.Retailer.RetailerId,
    );
    if (displayedRetailer) {
      displayedRetailer.icon = getMarkerIconRetailer(updatedRetailer);
      displayedRetailer.key = getRetailerKey(displayedRetailer);
    }
  });
});

watch(country, async (countryVal) => {
  if (!isMounted.value || !gmap.value) return;

  const newCountryLocation = countryLocations.value?.[countryVal as string];
  if (!newCountryLocation) return;

  const lat = Number.parseFloat(newCountryLocation.lat);
  const lng = Number.parseFloat(newCountryLocation.lng);
  const zoom = Number.parseFloat(newCountryLocation.zoom) || 6;

  googleMaps.defaultLat = lat;
  googleMaps.defaultLng = lng;
  googleMaps.defaultZoom = zoom;

  const map = await getMapPromise();
  changeZoom(zoom);
  map.panTo({ lat, lng });
});

watch(
  retailers,
  async (newRetailers) => {
    if (!newRetailers || newRetailers.length === 0) {
      displayedRetailers.value = [];
      lastRetailersHash.value = null;
      return;
    }

    const retailerClone = JSON.parse(JSON.stringify(newRetailers));
    const currentHash = hashLocations(retailerClone);
    if (lastRetailersHash.value === currentHash && displayedRetailers.value?.length > 0) {
      return;
    }
    lastRetailersHash.value = currentHash;

    retailerClone.forEach((retailer: any) => {
      retailer.position = getMarkerPosition(retailer);
      retailer.icon = getMarkerIconRetailer(retailer);
      retailer.key = getRetailerKey(retailer);
    });
    displayedRetailers.value = retailerClone;
  },
  { immediate: true },
);

const redrawMarkers = () => {
  cluster.value?.$clusterObject?.render();
};

const gmapOptions = () => {
  googleMaps.googleMapsOptions = {
    ...googleMapsOptions,
    mapId: fischer.gmapId(),
  };
  if (isSM.value) {
    googleMaps.googleMapsOptions.zoomControl = true;
  }
};

const hashLocations = (locations: any[]) => {
  const relevantData = locations
    .sort((a, b) => a.Distance || 999999 - b.Distance || 999999)
    .map((item) => `${item.Distance}:${item.Retailer?.RetailerId}`)
    .join('|');

  let hash = 2166136261;
  for (let i = 0; i < relevantData.length; i++) {
    hash ^= relevantData.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return hash >>> 0;
};

const getFacetsRetailerTypes = (result: any, variables: any) => {
  const retailerTypesArr = variables.retailerTypes || [];
  return result.GroupsRetailerType.map((group: any) => ({
    Name: group.Name,
    RecordCount: result.Retailers.filter((retailer: any) =>
      retailer.Retailer?.RetailerTypeList?.includes(group.Name),
    ).length,
    Selected: retailerTypesArr.includes(group.Name),
    Facets: group.SubGroup?.Elements
      ? group.SubGroup.Elements.map((subGroup: any) => ({
          Name: subGroup.Name,
          Selected: retailerTypesArr.includes(subGroup.Name),
          RecordCount: subGroup.RecordCount,
        }))
      : [],
  })).filter((group: any) => group.RecordCount);
};

const getMapPromise = () => {
  return gmap.value.$mapPromise;
};

const locationChanged = (value: any) => {
  location.value = value;
  locationError.value = false;

  if (value) {
    googleMaps.newLat = value.lat;
    googleMaps.newLng = value.lng;

    if (value.userLocation) {
      currentLocation.value = value;
    }
  } else {
    retailerName.value = '';
  }

  if (directions.active) {
    if (value) {
      directions.origin = value;
      updateRoute();
    }
  } else {
    sendGoogleMaps().then(() => {
      if (retailerName.value && retailerName.value.trim() !== '') {
        forceMarkersVisible();
      }
    });
  }
};

const forceMarkersVisible = async () => {
  if (!cluster.value?.clusterer) return;

  await new Promise<void>((resolve) => setTimeout(resolve, 0));
  await new Promise<void>((resolve) => setTimeout(resolve, 0));
  await new Promise<void>((resolve) => setTimeout(resolve, 150));

  const clusterer = cluster.value.clusterer;
  const map = await getMapPromise();
  const markers = clusterer.markers || [];

  requestAnimationFrame(() => {
    clusterer.clearMarkers();
    markers.forEach((marker: any) => {
      if (marker) marker.map = map;
    });
    clusterer.addMarkers(markers, true);
    clusterer.render();
  });
};

const retailerNameChanged = (value: string) => {
  if (retailerName.value === value) return;
  retailerName.value = value;
  sendGoogleMaps();
};

const setRange = (value: string) => {
  range.value = value;
  sendGoogleMaps();
};

const userLocationErrorChanged = (error: any) => {
  userLocationError.value = Boolean(error);
};

const getLanguageIsoCode = () => {
  if (props.defaultCountry) return props.defaultCountry;
  return fischer.languageIsoCode();
};

const activateResultItem = (retailer: any) => {
  clearNavigation();
  activateMarker(retailer);

  const position = getMarkerPosition(retailer);
  changeZoom(isSM.value ? 16 : 15);
  gmap.value.panTo(position);

  if (props.theme !== 'modal') {
    smoothScrollToElement(gmap.value.$el, {
      duration: 500,
      offset: 30,
    });
  } else {
    smoothScrollToElement(gmap.value.$el, {
      duration: 500,
      offset: 30,
      container: document.querySelector('.retail-search-dialog-fi .sweet-modal') || undefined,
    });
  }
};

const isStocked = (marker: any) => {
  return Boolean(marker.AvailabilityStatus && marker.AvailabilityStatus === 'AVAILABLE');
};

const activateMarker = (activeRetailerData: any) => {
  activeRetailer.value = activeRetailerData;
  clearNavigation();
  displayMarkerDetailInfo(activeRetailerData);
};

const clearSomeData = () => {
  googleMaps.facets.facetsRetailerTypes = null;
  googleMaps.facets.partnerTypes = null;
  googleMaps.facets.retailerTypes = null;
  googleMaps.facets.availabilityStatus = '';
  googleMaps.infoWindowOpened = false;
  noRetailersFound.value = false;
  activeRetailer.value = null;
  retailersCount.value = null;
};

const getDefaultGoogleMapsWithCountry = async () => {
  try {
    logDebug('getting countries');
    const fileName = '/countries-v3.json';
    const countriesResponse = await fetch(getAssetUrl(fileName));
    if (!countriesResponse.ok) {
      throw new Error(`Failed to fetch ${fileName}: ${countriesResponse.status}`);
    }
    const countriesData = await countriesResponse.json();
    logDebug('got countries');
    countryLocations.value = countriesData;

    const countryCode = googleMaps.languageIsoCode;
    const countryLocation = countryLocations.value[countryCode as string];

    if (countryLocation) {
      const lat = Number.parseFloat(countryLocation.lat);
      const lng = Number.parseFloat(countryLocation.lng);
      const zoom = Number.parseFloat(countryLocation.zoom) || 6;

      googleMaps.defaultLat = lat;
      googleMaps.defaultLng = lng;
      googleMaps.defaultZoom = zoom;
      googleMaps.lat = lat;
      googleMaps.lng = lng;
      googleMaps.zoom = zoom;
    } else {
      console.error(`Country '${countryCode}' not found in locations`);
    }

    country.value = countryCode;

    if (props.countryList.length && !props.countryList.some((c) => c.value === country.value)) {
      console.error(
        `Current country '${country.value}' not in country-list`,
        countryLocations.value,
      );
    }
  } catch (e) {
    console.error('Error getting countries', e);
  }
};

const changeZoom = (event: any) => {
  logDebug(`Zoom changed to ${event}`);
  if (throttledChangeZoom.value) {
    throttledChangeZoom.value(event);
  } else {
    googleMaps.zoom = event;
  }
};

const sendGoogleMaps = async () => {
  if (loading.value || isSearching.value) return;

  isSearching.value = true;

  try {
    if ((retailerName.value && retailerName.value.trim() !== '') || location.value) {
      if (googleMaps.newLat) {
        googleMaps.lat = googleMaps.newLat;
        googleMaps.lng = googleMaps.newLng;

        if (
          !_currentSearchBounds ||
          _lastSearchLat !== googleMaps.lat ||
          _lastSearchLng !== googleMaps.lng
        ) {
          const circle = new window.google.maps.Circle({
            zoom: googleMaps.zoom,
            radius: lengthUnitDistance(Number.parseFloat(range.value || '0')),
            center: {
              lat: googleMaps.lat,
              lng: googleMaps.lng,
            },
          });

          gmap.value.fitBounds(circle.getBounds());
          _currentSearchBounds = circle.getBounds();
          _lastSearchLat = googleMaps.lat;
          _lastSearchLng = googleMaps.lng;
        }
      } else {
        googleMaps.lat = googleMaps.defaultLat;
        googleMaps.lng = googleMaps.defaultLng;
        changeZoom(googleMaps.defaultZoom);
        _currentSearchBounds = null;
        _lastSearchLat = null;
        _lastSearchLng = null;
      }

      clearSomeData();
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      throttledGetRetailersByLocation.value?.();
    } else {
      await resetGoogleMaps();
    }
  } finally {
    isSearching.value = false;
  }
};

const getRetailersByLocation = (doNotUpdateFacets?: boolean) => {
  if (loading.value) return;

  loading.value = true;

  const variables = {
    brand: fischer.brandId(),
    language: fischer.catalogLocale(),
    latitude: googleMaps.lat === 0 ? '.000000' : googleMaps.lat.toString(),
    longitude: googleMaps.lng === 0 ? '.000000' : googleMaps.lng.toString(),
    distance: haveLocation.value ? range.value?.toString() : '50000',
    unit: fischer.lengthUnit(),
    retailerTypes: googleMaps.facets.retailerTypes,
    availabilityStatus: props.productIds ? 'AVAILABLE' : undefined,
    partnerTypes: googleMaps.facets.partnerTypes,
    query: retailerName.value && retailerName.value.trim() !== '' ? retailerName.value : undefined,
    articleNumbers: props.productIds,
    campaignRetailer: Boolean(fields?.campaignRetailers?.value),
    hiddenRetailers: Boolean(fields?.hiddenRetailers?.value),
  };

  const items = (variables.articleNumbers as any)
    ? (variables.articleNumbers as string).split(',').map((item) => ({ productId: item }))
    : undefined;
  const eventType = items ? 'retail_connect_local' : 'retailer_search';
  sendEvent(eventType, {
    searchTerm: location.value?.description,
    items,
    retailerQuery: variables.query,
    latitude: variables.latitude,
    longitude: variables.longitude,
    distance: variables.distance,
    unit: variables.unit,
    retailerTypes: variables.retailerTypes,
    availabilityStatus: variables.availabilityStatus,
    partnerTypes: variables.partnerTypes,
    campaignRetailer: (variables as any).campaignRetailers,
    hiddenRetailers: variables.hiddenRetailers,
  });

  handle.client
    .query(GETRETAILERSEARCH, variables, { requestPolicy: 'network-only' })
    .toPromise()
    .then(async (response: any) => {
      if (response.error) {
        noRetailersFound.value = true;
        console.error(response.error);
        return;
      }
      const retailerSearchResponse = response.data?.getRetailerSearch;

      if (retailerSearchResponse) {
        if (!doNotUpdateFacets && retailerSearchResponse.GroupsRetailerType) {
          googleMaps.facets.facetsRetailerTypes = getFacetsRetailerTypes(
            retailerSearchResponse,
            variables,
          );
        }

        retailers.value = retailerSearchResponse.Retailers;
        retailersCount.value = retailerSearchResponse.RecordCount;
      }

      noRetailersFound.value = !(retailersCount.value > 0);
    })
    .catch((err: any) => {
      noRetailersFound.value = true;
      console.error(err);
    })
    .finally(() => {
      loading.value = false;
      isSearching.value = false;
    });
};

const getMarkerPosition = (marker: any) => ({
  lat: Number.parseFloat(marker.Retailer.Address.Latitude),
  lng: Number.parseFloat(marker.Retailer.Address.Longitude),
});

const isActiveRetailer = (retailer: any) =>
  retailer.Retailer.RetailerId === activeRetailer.value?.Retailer?.RetailerId;

const getRetailerKey = (retailer: any) =>
  `${retailer.Retailer.RetailerId}${isActiveRetailer(retailer)}`;

const getMarkerIconRetailer = (retailer: any) =>
  getRetailerMarkerIcon(
    isActiveRetailer(retailer),
    haveArticles.value,
    isStocked(retailer),
    getAssetUrl,
  );

const getMarkerIconDirection = (marker: any) => getDirectionsMarkerIcon(marker, getAssetUrl);

const closeInfoWindow = () => {
  googleMaps.infoWindowOpened = false;
  if (activeRetailer.value) activeRetailer.value = null;
};

const displayMarkerDetailInfo = (marker: any) => {
  googleMaps.infoWindowOpened = true;
  googleMaps.marker.Retailer = marker.Retailer;
  googleMaps.marker.Distance = marker.Distance;
  googleMaps.marker.Unit = marker.Unit;
  googleMaps.marker.IsStocked = isStocked(marker);
  googleMaps.marker.partnerType = Boolean(marker.Retailer.PartnerType);
};

const retailerTypeChanged = (types: any) => {
  if (!types) {
    googleMaps.facets.retailerTypes = null;
    googleMaps.facets.partnerTypes = null;
    return;
  }

  const { partners, retailers: retailerArr } = types;
  if (retailerArr.length) {
    googleMaps.facets.retailerTypes = retailerArr.map((el: any) => el.name);
    const circle = new window.google.maps.Circle({
      zoom: googleMaps.zoom,
      radius: lengthUnitDistance(Number.parseFloat(range.value || '0')),
      center: {
        lat: googleMaps.newLat,
        lng: googleMaps.newLng,
      },
    });
    gmap.value.fitBounds(circle.getBounds());
  } else {
    googleMaps.facets.retailerTypes = null;
  }

  googleMaps.facets.partnerTypes = partners.length ? partners.map((el: any) => el.name) : null;

  closeInfoWindow();
  getRetailersByLocation(true);
};

const resetGoogleMaps = async () => {
  clearSomeData();
  clearNavigation();
  retailerName.value = '';
  googleMaps.lat = 0;
  googleMaps.lng = 0;
  googleMaps.newLat = 0;
  googleMaps.newLng = 0;
  googleMaps.lat = googleMaps.defaultLat;
  googleMaps.lng = googleMaps.defaultLng;
  googleMaps.facets.retailerTypes = null;
  _currentSearchBounds = null;

  if (cluster.value?.clusterer) cluster.value.clearMarkers();

  changeZoom(googleMaps.defaultZoom);
  const map = await getMapPromise();
  map.panTo({ lat: googleMaps.lat, lng: googleMaps.lng });
  map.zoom = googleMaps.zoom;
};

const navigateTo = (data: any) => {
  if (directions.loading) return;

  clearNavigation();
  directions.active = true;
  directions.destination = data;

  if (location.value) {
    directions.origin = location.value;
    setRoute();
  } else {
    locationError.value = true;
  }
};

const updateRoute = () => {
  directions.markers = [];
  if (directionsDisplay.value) {
    directionsDisplay.value.setDirections({ routes: [] });
  }
  setRoute();
};

const setRoute = () => {
  const map = gmap.value.$mapObject;
  if (!directionsDisplay.value) {
    directionsDisplay.value = new window.google.maps.DirectionsRenderer({
      map,
      suppressMarkers: true,
    });
  }

  directionsService.value = new window.google.maps.DirectionsService();
  const { origin, destination, markers, travelMode } = directions;

  directionsService.value.route(
    { origin, destination, travelMode },
    (response: any, directionsStatus: string) => {
      if (directionsStatus !== 'OK') return;
      directionsDisplay.value.setDirections(response);
      const route = response.routes[0].legs[0];
      routeData.value = route;

      markers.push(
        { name: 'Start', position: route.start_location, draggable: true, icon: 'Start' },
        { name: 'End', position: route.end_location, draggable: false, icon: 'End' },
      );
    },
  );
};

const closeDirections = () => {
  retailerName.value = '';
  clearNavigation();
  locationChanged(null);
};

const clearNavigation = () => {
  if (!directions.active) return;

  if (directionsDisplay.value) {
    directionsDisplay.value.setDirections({ routes: [] });
  }

  directions.loading = false;
  directions.active = false;
  directions.origin = null;
  directions.destination = null;
  directions.markers = [];
};

const markerDragged = (data: any) => {
  directions.origin.lat = data.latLng.lat();
  directions.origin.lng = data.latLng.lng();
  updateRoute();
};

const openGoogleMaps = () => {
  if (IS_SSR) return;

  const { origin, destination, travelMode } = directions;
  const params = `origin=${origin.description}&destination=${
    destination.description
  }&travelmode=${travelMode.toLowerCase()}`;
  // @ts-expect-error experimental api: userAgentData
  const platform = navigator?.userAgentData?.platform;
  let url = `https://www.google.com/maps/dir/?api=1&${params}`;

  if (platform && ['iPhone', 'iPod', 'iPad'].some((el) => platform.includes(el))) {
    url = `maps://www.google.com/maps/dir/?api=1&${params}`;
  }

  if (fischer.app.isAppView()) {
    sendAppEvent('open-external-url', { url });
  } else {
    window.open(url);
  }
};

const ensureMapInitialized = async () => {
  await new Promise<void>((resolve) => setTimeout(resolve, 0));
  try {
    const map = await getMapPromise();
    if (map) {
      map.setCenter({ lat: googleMaps.lat, lng: googleMaps.lng });
      map.setZoom(googleMaps.zoom);
    } else {
      console.error('Map object not available after promise resolved');
    }
  } catch (error) {
    console.error('Error waiting for map promise:', error);
  }
};

onMounted(async () => {
  range.value = props.defaultRange;
  const languageIsoCode = getLanguageIsoCode();
  googleMaps.languageIsoCode = languageIsoCode;
  googleMaps.autoCompleteOptions.componentRestrictions.country = [
    languageIsoCode,
    ...countries.value,
  ];

  gmapOptions();
  if (IS_SHOWROOM) {
    const retailerSearchResponse = await fetch('/__mocks__/data/retailerSearch.json');
    if (!retailerSearchResponse.ok) {
      throw new Error(`Failed to fetch retailerSearch.json: ${retailerSearchResponse.status}`);
    }
    showroomFallbackRetailer.value = await retailerSearchResponse.json();
  }

  throttledGetRetailersByLocation.value = throttle(400, getRetailersByLocation);
  throttledChangeZoom.value = throttle(100, (zoom: number) => {
    googleMaps.zoom = zoom;
  });
  await getDefaultGoogleMapsWithCountry();

  isMounted.value = true;
  await new Promise<void>((resolve) => setTimeout(resolve, 0));

  await ensureMapInitialized();
});

onBeforeUnmount(() => {
  if (directionsDisplay.value) {
    directionsDisplay.value.setMap(null);
  }

  if (throttledGetRetailersByLocation.value) {
    throttledGetRetailersByLocation.value.cancel();
  }

  if (cluster.value?.clusterer) {
    cluster.value.clearMarkers();
  }

  _currentSearchBounds = null;
});
</script>

<style scoped src="./retailer-search-fi" />

<style src="./retailer-search-fi-unscoped" />
